import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRight, Check, LayoutDashboard, Shield, Sparkles } from 'lucide-react';
import { resolveLandingOrigin, resolvePanelLoginUrl, resolvePanelRegisterUrl } from '../utils/booking';
import { applySeo } from '../utils/seo';
import { buildCityPage } from '../data/conversionPages';
import '../styles/landing.css';
import '../styles/conversion-pages.css';

function CitySection({ title, text }) {
  return (
    <article className="conversion-feature-card">
      <h2>{title}</h2>
      <p>{text}</p>
    </article>
  );
}

export default function CityLandingPage() {
  const { city } = useParams();
  const page = buildCityPage(city);
  const panelRegisterUrl = resolvePanelRegisterUrl();
  const panelLoginUrl = resolvePanelLoginUrl();

  useEffect(() => {
    const landingOrigin = resolveLandingOrigin();
    const pageUrl = `${landingOrigin}${page.path}`;

    applySeo({
      title: page.title,
      description: page.description,
      keywords: page.keywords,
      robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
      canonical: pageUrl,
      ogTitle: page.title,
      ogDescription: page.description,
      ogUrl: pageUrl,
      themeColor: '#f8fafb',
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: page.title,
        description: page.description,
        url: pageUrl,
        inLanguage: 'pl-PL',
      },
    });
  }, [page]);

  return (
    <div className="landing-page conversion-page">
      <nav className="landing-nav conversion-nav" aria-label="Nawigacja główna">
        <a href="/" className="landing-nav-logo" aria-label="ClientWay - strona główna">
          <span className="landing-logo-icon"><LayoutDashboard size={18} /></span>
          <span className="landing-logo-text">Client<span>Way</span></span>
        </a>

        <div className="industry-nav-links">
          <a href="/">Strona główna</a>
          <a href="/dlaczego-za-darmo">Dlaczego za darmo</a>
          <a href={panelLoginUrl}>Zaloguj się</a>
          <a href={panelRegisterUrl} className="industry-nav-cta">Zacznij za darmo</a>
        </div>
      </nav>

      <div className="conversion-sticky-bar">
        <div className="landing-container conversion-sticky-inner">
          <div className="conversion-sticky-copy">
            <strong>Rezerwacje dla firm w {page.city}</strong>
            <span>0 zł abonamentu, 0 zł prowizji, 0 zł za SMS.</span>
          </div>
          <a href={panelRegisterUrl} className="conversion-sticky-cta">
            Załóż darmowe konto
            <ArrowRight size={14} />
          </a>
        </div>
      </div>

      <section className="conversion-hero">
        <div className="landing-container conversion-hero-inner">
          <div className="landing-badge">
            <Sparkles size={12} />
            {page.city}
          </div>
          <h1>{page.h1}</h1>
          <p className="conversion-hero-lead">{page.heroLead}</p>
          <p className="conversion-hero-intro">{page.intro}</p>
          <div className="landing-cta-buttons">
            <a href={panelRegisterUrl} className="landing-btn-cta-primary">
              Rozpocznij za darmo
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container conversion-trust-grid">
          {page.benefits.map((benefit) => (
            <div key={benefit} className="conversion-trust-pill">
              <Check size={14} />
              {benefit}
            </div>
          ))}
        </div>
      </section>

      <section className="landing-section landing-section-muted">
        <div className="landing-container">
          <div className="landing-section-header">
            <p className="landing-section-label">Funkcje kalendarza</p>
            <h2>Najprostszy sposób na rezerwacje w {page.city}</h2>
            <p>Gotowy start bez kosztów i bez żmudnej konfiguracji.</p>
          </div>

          <div className="conversion-feature-grid">
            {page.sections.map((section) => (
              <CitySection key={section.title} title={section.title} text={section.text} />
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container">
          <article className="conversion-migration-box">
            <div>
              <p className="landing-section-label">Zmień system w 2 minuty</p>
              <h2>{page.migrationBox?.title || 'Zmień system w 2 minuty'}</h2>
              <p>{page.migrationBox?.text || 'Używasz innego systemu? Pomożemy Ci zaimportować bazę klientów za darmo.'}</p>
            </div>
            <a href={panelRegisterUrl} className="landing-btn-cta-primary">
              {page.migrationBox?.cta || 'Załóż darmowe konto'}
              <ArrowRight size={16} />
            </a>
          </article>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-section-header">
            <p className="landing-section-label">Korzyści</p>
            <h2>Masz porządek w zapisach i mniej ręcznej roboty</h2>
            <p>ClientWay pomaga usługodawcom w {page.city} ogarnąć codzienny chaos bez płacenia za narzędzie.</p>
          </div>

          <div className="conversion-case-grid">
            {page.caseStudies.map((item) => (
              <article key={item.title} className="conversion-case-card">
                <div className="conversion-case-label">{item.title}</div>
                <blockquote>{item.quote}</blockquote>
                <p>{item.result}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section landing-section-muted">
        <div className="landing-container">
          <div className="landing-section-header">
            <p className="landing-section-label">FAQ</p>
            <h2>Najczęstsze pytania</h2>
            <p>Tak działa lokalna strona rezerwacji dla firm w {page.city}.</p>
          </div>

          <div className="conversion-faq-grid">
            {page.faqs.map((faq) => (
              <article key={faq.question} className="conversion-faq-card">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-final-cta">
        <div className="landing-container landing-cta-inner">
          <div className="landing-badge landing-badge-cta">
            <Shield size={12} />
            Darmowe dla usługodawców
          </div>
          <h2>Masz chaos w zapisach? Włącz darmowy kalendarz w {page.city}.</h2>
          <p>Rezerwacje 24/7, przypomnienia SMS i baza klientów bez abonamentu i bez prowizji.</p>
          <div className="landing-cta-buttons">
            <a href={panelRegisterUrl} className="landing-btn-cta-primary">
              Rozpocznij za darmo
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
